import { useState, useEffect } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";

interface UseInitialLoadTrackingProps<T extends BaseEntity> {
  data: any;
  filterContext: any;
}

/**
 * Tracks initial load state and manages accumulated data for infinite scroll
 * Resets accumulated data when filters change
 */
export function useInitialLoadTracking<T extends BaseEntity>({
  data,
  filterContext,
}: UseInitialLoadTrackingProps<T>) {
  // Track whether data has loaded at least once to distinguish between
  // initial load (show full-page loading) and filter updates (show overlay)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Track accumulated data for infinite scroll
  const [accumulatedData, setAccumulatedData] = useState<any[]>([]);

  // Set hasLoadedOnce to true after first data arrives
  useEffect(() => {
    if (data?.data && data.data.length > 0 && !hasLoadedOnce) {
      setHasLoadedOnce(true);
      // Reset accumulated data on initial load
      setAccumulatedData(data.data);
    }
  }, [data?.data, hasLoadedOnce]);

  // When filters or search changes, reset accumulated data
  useEffect(() => {
    setAccumulatedData([]);
  }, [filterContext?.filters.search, filterContext?.filters.simple, filterContext?.filters.advanced]);

  return {
    hasLoadedOnce,
    accumulatedData,
    setAccumulatedData,
  };
}
